<template>
  <div class="user-payment-container">
    <!-- 页面标题 -->
    <div class="page-header">
      <div>
        <h2>支付记录</h2>
        <p>核对订单、渠道、应付实付金额和支付状态</p>
      </div>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container">
      <el-icon class="is-loading">
        <Loading />
      </el-icon>
      <span>加载中...</span>
    </div>

    <!-- 数据展示 -->
    <div v-else-if="userRechargeRecords">
      <!-- 统计卡片 -->
      <div class="statistics-cards">
        <el-card class="stat-card total">
          <div class="stat-content">
            <div class="stat-icon">
              <el-icon><DocumentCopy /></el-icon>
            </div>
            <div class="stat-info">
              <div class="stat-label">订单总数</div>
              <div class="stat-value">{{ userRechargeRecords.total }}</div>
            </div>
          </div>
        </el-card>

        <el-card class="stat-card success">
          <div class="stat-content">
            <div class="stat-icon">
              <el-icon><CircleCheck /></el-icon>
            </div>
            <div class="stat-info">
              <div class="stat-label">成功订单</div>
              <div class="stat-value">{{ successCount }}</div>
            </div>
          </div>
        </el-card>

        <el-card class="stat-card amount">
          <div class="stat-content">
            <div class="stat-icon">
              <el-icon><Money /></el-icon>
            </div>
            <div class="stat-info">
              <div class="stat-label">应支付总额</div>
              <div class="stat-value">¥{{ totalExpectedAmount }}</div>
            </div>
          </div>
        </el-card>

        <el-card class="stat-card paid">
          <div class="stat-content">
            <div class="stat-icon">
              <el-icon><Wallet /></el-icon>
            </div>
            <div class="stat-info">
              <div class="stat-label">实际支付总额</div>
              <div class="stat-value">¥{{ totalPaidAmount }}</div>
            </div>
          </div>
        </el-card>
      </div>

      <!-- 筛选器 -->
      <div class="filter-section">
        <el-input
          v-model="keyword"
          placeholder="搜索订单号/用户名"
          clearable
          style="width: 250px"
          @clear="handleSearch"
          @keyup.enter="handleSearch"
        >
          <template #prefix>
            <el-icon><Search /></el-icon>
          </template>
        </el-input>

        <el-select
          v-model="filterChannel"
          placeholder="筛选支付渠道"
          clearable
          style="width: 200px; margin-left: 10px"
          @change="handleSearch"
        >
          <el-option label="全部" value="" />
          <el-option label="支付宝" value="alipay" />
          <el-option label="微信支付" value="wechat" />
        </el-select>

        <el-select
          v-model="filterStatus"
          placeholder="筛选支付状态"
          clearable
          style="width: 200px; margin-left: 10px"
          @change="handleSearch"
        >
          <el-option label="全部" value="" />
          <el-option label="支付成功" value="success" />
          <el-option label="支付失败" value="failed" />
          <el-option label="待支付" value="pending" />
        </el-select>

        <el-button
          type="primary"
          style="margin-left: 10px"
          @click="handleSearch"
        >
          <el-icon><Search /></el-icon>
          搜索
        </el-button>
      </div>

      <!-- 支付记录表格 -->
      <el-card class="table-card">
        <el-table :data="userRechargeRecords.data" stripe style="width: 100%">
          <!-- 用户信息 -->
          <el-table-column label="用户" width="200">
            <template #default="{ row }">
              <div class="user-info">
                <el-avatar :src="row.user.avatar" :size="40" />
                <div class="user-details">
                  <div class="username">{{ row.user.username }}</div>
                  <div class="user-email">{{ row.user.email || '-' }}</div>
                </div>
              </div>
            </template>
          </el-table-column>

          <!-- 订单信息 -->
          <el-table-column label="订单号" width="280">
            <template #default="{ row }">
              <div class="order-info">
                <div class="order-id">{{ row.orderId }}</div>
                <div class="order-id-small">ID: {{ row._id }}</div>
              </div>
            </template>
          </el-table-column>

          <!-- 套餐信息 -->
          <el-table-column label="套餐信息" width="200">
            <template #default="{ row }">
              <div class="plan-info">
                <div class="plan-name">{{ row.planName }}</div>
                <div class="plan-description">{{ row.description }}</div>
              </div>
            </template>
          </el-table-column>

          <!-- 支付渠道 -->
          <el-table-column label="支付渠道" width="120">
            <template #default="{ row }">
              <el-tag :type="getChannelType(row.channel)" effect="dark">
                {{ row.channelName }}
              </el-tag>
            </template>
          </el-table-column>

          <!-- 金额对比 -->
          <el-table-column label="金额信息" width="200">
            <template #default="{ row }">
              <div class="amount-info">
                <div class="expected-amount">
                  <span class="label">应支付:</span>
                  <span class="value">¥{{ row.amount }}</span>
                </div>
                <div
                  class="paid-amount"
                  :class="{
                    'amount-diff': row.amount !== row.transaction?.amount
                  }"
                >
                  <span class="label">实支付:</span>
                  <span class="value"
                    >¥{{ row.transaction?.amount || '-' }}</span
                  >
                  <el-tooltip
                    v-if="row.amount !== row.transaction?.amount"
                    content="应支付金额与实际支付金额不一致"
                    placement="top"
                  >
                    <el-icon class="warning-icon"><WarningFilled /></el-icon>
                  </el-tooltip>
                </div>
                <div
                  v-if="row.amount !== row.transaction?.amount"
                  class="amount-diff-text"
                >
                  差额: ¥{{
                    (row.amount - (row.transaction?.amount || 0)).toFixed(2)
                  }}
                </div>
              </div>
            </template>
          </el-table-column>

          <!-- 支付状态 -->
          <el-table-column label="支付状态" width="120">
            <template #default="{ row }">
              <el-tag :type="getStatusType(row.status)">
                {{ row.statusName }}
              </el-tag>
            </template>
          </el-table-column>

          <!-- 支付时间 -->
          <el-table-column label="支付时间" width="180">
            <template #default="{ row }">
              <div v-if="row.paidAt" class="time-info">
                {{ formatDate(row.paidAt) }}
              </div>
              <div v-else class="time-info empty">-</div>
            </template>
          </el-table-column>

          <!-- 创建时间 -->
          <el-table-column label="创建时间" width="180">
            <template #default="{ row }">
              {{ formatDate(row.createdAt) }}
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="pagination-container">
          <el-pagination
            v-model:current-page="page"
            v-model:page-size="limit"
            :page-sizes="[10, 20, 50, 100]"
            :total="userRechargeRecords.total"
            layout="total, sizes, prev, pager, next, jumper"
            @size-change="handleSizeChange"
            @current-change="handlePageChange"
          />
        </div>
      </el-card>
    </div>

    <!-- 无数据 -->
    <div v-else class="no-data">
      <el-empty description="暂无数据" />
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { getUserRechargeRecords } from '@/api/user'
import {
  DocumentCopy,
  CircleCheck,
  Money,
  Wallet,
  Search,
  Loading,
  WarningFilled
} from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'

const userRechargeRecords = ref(null)
const loading = ref(true)
const page = ref(1)
const limit = ref(20)
const keyword = ref('')
const filterChannel = ref('')
const filterStatus = ref('')

// 获取数据
const getUserRechargeRecordsData = async () => {
  try {
    loading.value = true
    const response = await getUserRechargeRecords(
      page.value,
      limit.value,
      keyword.value
    )
    userRechargeRecords.value = response
  } catch (error) {
    console.error('获取数据失败:', error)
    ElMessage.error('获取数据失败')
  } finally {
    loading.value = false
  }
}

// 成功订单数
const successCount = computed(() => {
  if (!userRechargeRecords.value?.data) return 0
  return userRechargeRecords.value.data.filter(
    (item) => item.status === 'success'
  ).length
})

// 应支付总额
const totalExpectedAmount = computed(() => {
  if (!userRechargeRecords.value?.data) return '0.00'
  const total = userRechargeRecords.value.data.reduce(
    (sum, item) => sum + (item.amount || 0),
    0
  )
  return total.toFixed(2)
})

// 实际支付总额
const totalPaidAmount = computed(() => {
  if (!userRechargeRecords.value?.data) return '0.00'
  const total = userRechargeRecords.value.data.reduce(
    (sum, item) => sum + (item.transaction?.amount || 0),
    0
  )
  return total.toFixed(2)
})

// 获取支付渠道类型
const getChannelType = (channel) => {
  const typeMap = {
    alipay: 'primary',
    wechat: 'success'
  }
  return typeMap[channel] || 'info'
}

// 获取状态类型
const getStatusType = (status) => {
  const typeMap = {
    success: 'success',
    failed: 'danger',
    pending: 'warning',
    cancelled: 'info'
  }
  return typeMap[status] || 'info'
}

// 格式化日期
const formatDate = (dateString) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  })
}

// 搜索
const handleSearch = () => {
  page.value = 1
  getUserRechargeRecordsData()
}

// 分页大小改变
const handleSizeChange = () => {
  page.value = 1
  getUserRechargeRecordsData()
}

// 页码改变
const handlePageChange = () => {
  getUserRechargeRecordsData()
}

// 初始化加载数据
getUserRechargeRecordsData()
</script>

<style scoped lang="scss">
.user-payment-container {
  padding: 0;
  background-color: transparent;
  min-height: 100%;

  .page-header {
    margin-bottom: 20px;

    h2 {
      margin: 0;
      font-size: 24px;
      font-weight: 600;
      color: #303133;
    }
  }

  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 400px;
    color: #909399;

    .el-icon {
      font-size: 48px;
      margin-bottom: 16px;
    }

    span {
      font-size: 16px;
    }
  }

  .statistics-cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 20px;
    margin-bottom: 20px;

    .stat-card {
      cursor: pointer;
      transition: transform 0.3s, box-shadow 0.3s;

      .stat-content {
        display: flex;
        align-items: center;
        gap: 16px;

        .stat-icon {
          width: 56px;
          height: 56px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 28px;
        }

        .stat-info {
          flex: 1;

          .stat-label {
            font-size: 14px;
            color: #909399;
            margin-bottom: 8px;
          }

          .stat-value {
            font-size: 28px;
            font-weight: 600;
            color: #303133;
          }
        }
      }

      &.total .stat-icon {
        color: var(--admin-color-primary);
      }

      &.success .stat-icon {
        color: var(--admin-color-primary);
      }

      &.amount .stat-icon {
        color: var(--admin-color-primary);
      }

      &.paid .stat-icon {
        color: var(--admin-color-primary);
      }
    }
  }

  .filter-section {
    margin-bottom: 20px;
    display: flex;
    gap: 10px;
    align-items: center;
  }

  .table-card {
    .user-info {
      display: flex;
      align-items: center;
      gap: 12px;

      .user-details {
        flex: 1;

        .username {
          font-weight: 500;
          color: #303133;
          margin-bottom: 4px;
        }

        .user-email {
          font-size: 12px;
          color: #909399;
        }
      }
    }

    .order-info {
      .order-id {
        font-weight: 500;
        color: #303133;
        font-size: 13px;
        margin-bottom: 4px;
        word-break: break-all;
      }

      .order-id-small {
        font-size: 11px;
        color: #909399;
        word-break: break-all;
      }
    }

    .plan-info {
      .plan-name {
        font-weight: 500;
        color: #303133;
        margin-bottom: 4px;
        font-size: 13px;
      }

      .plan-description {
        font-size: 12px;
        color: #909399;
        line-height: 1.4;
      }
    }

    .amount-info {
      .expected-amount,
      .paid-amount {
        display: flex;
        align-items: center;
        gap: 4px;
        margin-bottom: 4px;
        font-size: 13px;

        .label {
          color: #909399;
        }

        .value {
          font-weight: 600;
          color: #303133;
        }

        .warning-icon {
          color: #f56c6c;
          font-size: 14px;
          animation: pulse 2s infinite;
        }
      }

      .paid-amount.amount-diff {
        .value {
          color: #f56c6c;
        }
      }

      .amount-diff-text {
        font-size: 11px;
        color: #f56c6c;
        margin-top: 2px;
      }
    }

    .time-info {
      font-size: 13px;
      color: #606266;

      &.empty {
        color: #c0c4cc;
      }
    }

    .pagination-container {
      margin-top: 20px;
      display: flex;
      justify-content: flex-end;
    }
  }

  .no-data {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 400px;
    background: white;
    border-radius: 8px;
  }
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}
</style>
